import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Layout from "../layouts/Layout";
import { API_BASE_URL, FONT_UI, SKIN_THEMES } from "../layouts/constants";

const DEFAULT_CENTER = [20.5937, 78.9629];

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function api(path) {
  const base = API_BASE_URL.replace(/\/$/, "");
  if (base.endsWith("/api") && path.startsWith("/api/")) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }
  return `${base}${path}`;
}

function toNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmtKm(m) {
  return (Number(m || 0) / 1000).toFixed(1);
}

function fmtKph(mps) {
  return Math.round(Number(mps || 0) * 3.6);
}

function parseUtcToIST(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts !== "string") return null;

  const s = ts.trim();
  if (!s) return null;

  const utcStr = /Z$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`;
  const d = new Date(utcStr);

  return Number.isNaN(d.getTime()) ? null : d;
}

function parseDbIst(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts !== "string") return null;

  const s = ts.trim();
  if (!s) return null;

  const clean = s.replace("T", " ").replace("Z", "").split(".")[0];
  const [datePart, timePart] = clean.split(" ");

  if (!datePart || !timePart) return null;

  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute, second = 0] = timePart.split(":").map(Number);

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  ) {
    return null;
  }

  const d = new Date(year, month - 1, day, hour, minute, second);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateObj(d) {
  if (!d) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  })
    .format(d)
    .replace(",", "");
}

function formatUtcAsIST(ts) {
  return formatDateObj(parseUtcToIST(ts));
}

function humanDuration(startRaw, endRaw) {
  const startD = parseDbIst(startRaw);
  const endD = endRaw ? parseUtcToIST(endRaw) : new Date();

  if (!startD || !endD) return "—";

  const ms = endD.getTime() - startD.getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";

  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;

  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${ss}s`;
  return `${ss}s`;
}

const ESCORT_POLL_MS = 2000;
const MAX_ACCURACY_M = 50;
const MAX_JUMP_M = 120;

function pointKey(p) {
  return String(p?.id ?? `${p?.lat?.toFixed(6)},${p?.lng?.toFixed(6)}`);
}

function haversineMeters(a, b) {
  const R = 6371000;
  const toRad = (v) => (Number(v) * Math.PI) / 180;

  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function cleanEscortTrack(points = []) {
  const ordered = points
    .map((p) => ({
      ...p,
      lat: toNum(p.lat),
      lng: toNum(p.lng),
      accuracy_m: toNum(p.accuracy_m),
      speed_mps: toNum(p.speed_mps),
      _key: pointKey(p),
    }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
    .filter((p) => p.accuracy_m === null || p.accuracy_m <= MAX_ACCURACY_M);

  const cleaned = [];

  for (const p of ordered) {
    if (!cleaned.length) {
      cleaned.push(p);
      continue;
    }

    const prev = cleaned[cleaned.length - 1];
    const dist = haversineMeters(prev, p);

    if (dist > MAX_JUMP_M) {
      console.warn("GPS spike skipped:", prev.id, "->", p.id, dist.toFixed(1));
      continue;
    }

    cleaned.push(p);
  }

  return cleaned;
}

function vehicleSvg(color = "#111827", size = 54) {
  return `
    <svg viewBox="0 0 64 32" width="${size}" height="${size}" fill="${color}" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="10" width="30" height="12" rx="2"></rect>
      <path d="M34 10h12l6 6v6H34z"></path>
      <circle cx="14" cy="26" r="4" fill="#1f2937"></circle>
      <circle cx="42" cy="26" r="4" fill="#1f2937"></circle>
    </svg>
  `;
}

function vehicleIcon(color = "#111827", size = 54) {
  return L.divIcon({
    className: "escort-user-icon",
    html: vehicleSvg(color, size),
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function pinIcon({ color = "#2563eb", label = "●", size = 34 }) {
  return L.divIcon({
    className: "escort-pin-icon",
    html: `
      <div style="width:${size}px;height:${size}px;border-radius:999px;background:${color};color:white;display:flex;align-items:center;justify-content:center;font-size:18px;font-weight:800;border:3px solid white;box-shadow:0 3px 10px rgba(0,0,0,0.35);">
        ${label}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function checkpointIcon(index) {
  const size = 32;
  const label = String(index + 1);

  return L.divIcon({
    className: "escort-checkpoint-icon",
    html: `
      <div style="width:${size}px;height:${size}px;border-radius:50%;background:#f97316;color:white;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:800;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.30);font-family:system-ui,sans-serif;">
        ${label}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

const fromIcon = () => pinIcon({ color: "#16a34a", label: "F" });
const toIcon = () => pinIcon({ color: "#dc2626", label: "T" });
const stopIcon = () => pinIcon({ color: "#7c3aed", label: "S" });

export default function EscortLiveTracking() {
  const navigate = useNavigate();
  const location = useLocation();
  const skin = useSkin();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const memberId = params.get("member_id") || "";
  const runId = params.get("run_id") || "";
  const limit = params.get("limit") || "2000";

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  const mapRef = useRef(null);
  const mapBoxRef = useRef(null);

  const latestMarkerRef = useRef(null);
  const fromMarkerRef = useRef(null);
  const toMarkerRef = useRef(null);
  const stopMarkerRef = useRef(null);
  const checkpointMarkersRef = useRef([]);

  const segmentLayersRef = useRef([]);
  const trackLineRef = useRef(null);
  const renderedLatLngsRef = useRef([]);
  const renderedKeysRef = useRef(new Set());

  const latestLatLngRef = useRef(null);
  const livePointQueueRef = useRef([]);
  const liveAnimatingRef = useRef(false);
  const lastAnimatedKeyRef = useRef(null);

  const fittedOnceRef = useRef(false);
  const pollRef = useRef(null);

  const stableFromRef = useRef(null);
  const stableToRef = useRef(null);

  const checkpointsRef = useRef([]);
  const checkpointsFetchedRef = useRef(false);

  const [followLive, setFollowLive] = useState(true);
  const followLiveRef = useRef(true);

  const [status, setStatus] = useState({
    label: "—",
    className: "bg-slate-200 text-slate-800",
  });

  const [meta, setMeta] = useState({
    title: "—",
    from: "—",
    to: "—",
    vehicle: "—",
    lastTs: "—",
    distanceKm: "0.0",
    speedKph: "0",
    duration: "—",
    pointsCount: "0",
  });

  const [checkpointCount, setCheckpointCount] = useState(0);

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const apiUrl = useMemo(() => {
    const q = new URLSearchParams({
      member_id: memberId,
      limit,
      include_segments: "1",
    });

    if (runId) q.set("run_id", runId);

    return api(`/api/escort/live/tracking?${q.toString()}`);
  }, [memberId, runId, limit]);

  const lockStablePins = useCallback((run) => {
    if (!run) return;

    if (!stableFromRef.current) {
      const lat = toNum(run.start_lat ?? run.from_lat);
      const lng = toNum(run.start_lng ?? run.from_lng);

      if (lat !== null && lng !== null) {
        stableFromRef.current = {
          lat,
          lng,
          name: run.from_name || "From",
        };
      }
    }

    if (!stableToRef.current) {
      const lat = toNum(run.end_lat ?? run.to_lat);
      const lng = toNum(run.end_lng ?? run.to_lng);

      if (lat !== null && lng !== null) {
        stableToRef.current = {
          lat,
          lng,
          name: run.to_name || "To",
        };
      }
    }
  }, []);

  const fetchCheckpoints = useCallback(async (routeIdForCheckpoints) => {
    if (!routeIdForCheckpoints) return [];

    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(
        api(`/api/checkpoints?route_id=${encodeURIComponent(routeIdForCheckpoints)}`),
        {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        }
      );

      if (!res.ok) return [];

      const data = await res.json();
      const rows = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];

      return rows.filter((cp) => {
        const lat = toNum(cp.lat ?? cp.latitude);
        const lng = toNum(cp.lng ?? cp.longitude);
        return lat !== null && lng !== null;
      });
    } catch (err) {
      console.warn("Checkpoint fetch skipped:", err.message);
      return [];
    }
  }, []);

  const drawCheckpoints = useCallback((map) => {
    checkpointMarkersRef.current.forEach((m) => map.removeLayer(m));
    checkpointMarkersRef.current = [];

    const checkpoints = checkpointsRef.current;
    if (!checkpoints || checkpoints.length === 0) return;

    checkpoints.forEach((cp, index) => {
      const lat = toNum(cp.lat ?? cp.latitude);
      const lng = toNum(cp.lng ?? cp.longitude);

      if (lat === null || lng === null) return;

      const name = cp.checkpoint_name || cp.name || `Checkpoint ${index + 1}`;
      const order = cp.checkpoint_order ?? cp.seq_no ?? index + 1;

      const marker = L.marker([lat, lng], {
        icon: checkpointIcon(index),
        zIndexOffset: 800,
      })
        .addTo(map)
        .bindTooltip(
          `<div style="font-family:system-ui,sans-serif;min-width:120px;">
            <div style="font-weight:700;font-size:13px;color:#ea580c;">Checkpoint ${order}</div>
            <div style="font-size:12px;color:#374151;margin-top:2px;">${name}</div>
          </div>`,
          { direction: "top" }
        );

      checkpointMarkersRef.current.push(marker);
    });
  }, []);

  const fetchDashboardData = useCallback(async () => {
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(api("/api/dashboard/admin/dashboard"), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to fetch dashboard");

      const json = await res.json();

      setDashboardData((prev) => ({
        ...prev,
        ...(json?.data?.companyDetails || {}),
      }));
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      toast.error(error.message || "Failed to load company details.");
      setPageError(error.message || "Failed to load company details.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const clearLayers = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    [latestMarkerRef, fromMarkerRef, toMarkerRef, stopMarkerRef].forEach((ref) => {
      if (ref.current) {
        map.removeLayer(ref.current);
        ref.current = null;
      }
    });

    segmentLayersRef.current.forEach((layer) => map.removeLayer(layer));
    segmentLayersRef.current = [];

    trackLineRef.current = null;
    renderedLatLngsRef.current = [];
    renderedKeysRef.current = new Set();

    livePointQueueRef.current = [];
    liveAnimatingRef.current = false;
    lastAnimatedKeyRef.current = null;
    latestLatLngRef.current = null;
  }, []);

  const setRunStatus = useCallback((runStatus) => {
    const s = String(runStatus || "").toLowerCase();

    if (s === "active") {
      setStatus({
        label: "LIVE",
        className: "bg-emerald-500 text-white",
      });
    } else if (["stopped", "abandoned", "completed"].includes(s)) {
      setStatus({
        label: s.toUpperCase(),
        className: "bg-red-100 text-red-700",
      });
    } else {
      setStatus({
        label: "—",
        className: "bg-slate-200 text-slate-800",
      });
    }
  }, []);

  const placeEndpointMarkers = useCallback((map, bounds) => {
    const from = stableFromRef.current;
    const to = stableToRef.current;

    if (from) {
      if (fromMarkerRef.current) map.removeLayer(fromMarkerRef.current);

      fromMarkerRef.current = L.marker([from.lat, from.lng], {
        icon: fromIcon(),
        zIndexOffset: 900,
      })
        .addTo(map)
        .bindTooltip(`From<br>${from.name}`, { direction: "top" });

      bounds.extend([from.lat, from.lng]);
    }

    if (to) {
      if (toMarkerRef.current) map.removeLayer(toMarkerRef.current);

      toMarkerRef.current = L.marker([to.lat, to.lng], {
        icon: toIcon(),
        zIndexOffset: 900,
      })
        .addTo(map)
        .bindTooltip(`To<br>${to.name}`, { direction: "top" });

      bounds.extend([to.lat, to.lng]);
    }
  }, []);

  const placeStopMarker = useCallback((map, bounds, lat, lng, label) => {
    if (lat === null || lng === null) return;

    if (stopMarkerRef.current) map.removeLayer(stopMarkerRef.current);

    stopMarkerRef.current = L.marker([lat, lng], {
      icon: stopIcon(),
      zIndexOffset: 1000,
    })
      .addTo(map)
      .bindTooltip(`Stopped Here<br>${label || ""}`, { direction: "top" });

    bounds.extend([lat, lng]);
  }, []);

  const drawPlannedPins = useCallback(
    (run) => {
      const map = mapRef.current;
      if (!map || !run) return;

      clearLayers();
      lockStablePins(run);

      const bounds = L.latLngBounds();

      placeEndpointMarkers(map, bounds);
      drawCheckpoints(map);

      if (bounds.isValid()) map.fitBounds(bounds.pad(0.2));
      else map.setView(DEFAULT_CENTER, 5);
    },
    [clearLayers, lockStablePins, placeEndpointMarkers, drawCheckpoints]
  );

  const animateMarkerTo = useCallback((marker, from, to, duration = 900) => {
    const start = performance.now();

    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);

      const lat = from.lat + (to.lat - from.lat) * t;
      const lng = from.lng + (to.lng - from.lng) * t;

      marker.setLatLng([lat, lng]);

      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        latestLatLngRef.current = [to.lat, to.lng];
        liveAnimatingRef.current = false;
        lastAnimatedKeyRef.current = to._key;
        playLiveQueueRef.current();
      }
    };

    requestAnimationFrame(step);
  }, []);

  const playLiveQueueRef = useRef(() => {});

  const playLiveQueue = useCallback(() => {
    const map = mapRef.current;
    const marker = latestMarkerRef.current;

    if (!map || !marker || liveAnimatingRef.current) return;

    const nextPoint = livePointQueueRef.current.shift();
    if (!nextPoint) return;

    const current = marker.getLatLng();

    liveAnimatingRef.current = true;

    animateMarkerTo(
      marker,
      { lat: current.lat, lng: current.lng },
      nextPoint,
      1000
    );

    if (followLiveRef.current) {
      map.panTo([nextPoint.lat, nextPoint.lng], {
        animate: true,
        duration: 0.8,
      });
    }
  }, [animateMarkerTo]);

  useEffect(() => {
    playLiveQueueRef.current = playLiveQueue;
  }, [playLiveQueue]);

  const drawTrack = useCallback(
    (data, runStatus) => {
      const map = mapRef.current;
      if (!map) return;

      const payload = data || {};
      const run = payload.run || {};
      const rawPoints = Array.isArray(payload?.points) ? payload.points : [];
      const points = cleanEscortTrack(rawPoints);

      const bounds = L.latLngBounds();

      lockStablePins(run);

      const isStopped = ["stopped", "abandoned", "completed"].includes(
        String(runStatus || "").toLowerCase()
      );

      placeEndpointMarkers(map, bounds);
      drawCheckpoints(map);

      if (!trackLineRef.current) {
        trackLineRef.current = L.polyline([], {
          color: "#0ea5e9",
          weight: 6,
          opacity: 0.95,
          lineJoin: "round",
          lineCap: "round",
        }).addTo(map);
      }

      renderedLatLngsRef.current = [];
      renderedKeysRef.current = new Set();

      points.forEach((p) => {
        if (!renderedKeysRef.current.has(p._key)) {
          renderedKeysRef.current.add(p._key);
          renderedLatLngsRef.current.push([p.lat, p.lng]);
          bounds.extend([p.lat, p.lng]);
        }
      });

      trackLineRef.current.setLatLngs(renderedLatLngsRef.current);

      const latestPoint = points[points.length - 1];

      if (latestPoint && !isStopped) {
        if (!latestMarkerRef.current) {
          const firstPoint = points[0];

          latestMarkerRef.current = L.marker([firstPoint.lat, firstPoint.lng], {
            icon: vehicleIcon("#111827", 54),
            zIndexOffset: 1200,
          })
            .bindTooltip("Live", { direction: "top" })
            .addTo(map);

          latestLatLngRef.current = [firstPoint.lat, firstPoint.lng];
          lastAnimatedKeyRef.current = firstPoint._key;

          livePointQueueRef.current = points.slice(1);
          playLiveQueue();
        } else {
          const currentIndex = points.findIndex(
            (p) => p._key === lastAnimatedKeyRef.current
          );

          const newPoints =
            currentIndex >= 0 ? points.slice(currentIndex + 1) : [latestPoint];

          if (newPoints.length > 0) {
            const existingKeys = new Set(livePointQueueRef.current.map((p) => p._key));
            newPoints.forEach((p) => {
              if (!existingKeys.has(p._key)) livePointQueueRef.current.push(p);
            });

            playLiveQueue();
          }
        }
      }

      if (isStopped && latestPoint) {
        if (latestMarkerRef.current) {
          map.removeLayer(latestMarkerRef.current);
          latestMarkerRef.current = null;
        }

        livePointQueueRef.current = [];
        liveAnimatingRef.current = false;

        placeStopMarker(
          map,
          bounds,
          latestPoint.lat,
          latestPoint.lng,
          formatUtcAsIST(latestPoint.ts || latestPoint.recorded_at)
        );
      }

      if (bounds.isValid() && !fittedOnceRef.current) {
        map.fitBounds(bounds.pad(0.18));
        fittedOnceRef.current = true;
      }
    },
    [
      lockStablePins,
      placeEndpointMarkers,
      drawCheckpoints,
      placeStopMarker,
      playLiveQueue,
    ]
  );

  const refresh = useCallback(async () => {
    if (!memberId) {
      setStatus({
        label: "member_id required",
        className: "bg-red-100 text-red-700",
      });
      toast.error("Member ID is required for tracking");
      return;
    }

    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(apiUrl, {
        cache: "no-store",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      if (!res.ok || data?.ok === false || data?.success === false) {
        throw new Error(data?.error || data?.message || "API error");
      }

      const payload = data?.data || data;
      const run = payload?.run || {};
      const latest = payload?.latest || {};
      const runStatus = String(run?.status || "").toLowerCase();

      if (!checkpointsFetchedRef.current) {
        checkpointsFetchedRef.current = true;

        const routeIdForCp = run?.route_id || runId;
        const cps = await fetchCheckpoints(routeIdForCp);

        checkpointsRef.current = cps;
        setCheckpointCount(cps.length);
      }

      const lastTsRaw = latest?.ts || latest?.recorded_at || latest?.created_at || "";
      const lastTsDisplay = lastTsRaw ? formatUtcAsIST(lastTsRaw) : "—";

      setMeta({
        title: run?.title || "—",
        from: run?.from_name || "—",
        to: run?.to_name || "—",
        vehicle: run?.vehicle || "—",
        lastTs: lastTsDisplay,
        distanceKm: fmtKm(run?.distance_m || payload?.distance_m || 0),
        speedKph: String(fmtKph(latest?.speed_mps || run?.speed_mps || 0)),
        duration: humanDuration(run?.start_time, run?.end_time),
        pointsCount: String(payload?.total_points || payload?.points?.length || 0),
      });

      setRunStatus(runStatus);

      if (runStatus === "planned" && !payload?.latest) {
        drawPlannedPins(run);
      } else {
        drawTrack(payload, runStatus);
      }
    } catch (err) {
      console.error("Live tracking error:", err);

      setStatus({
        label: err.message || "API error",
        className: "bg-red-100 text-red-700",
      });

      toast.error(err.message || "Failed to fetch tracking data");
    }
  }, [
    apiUrl,
    memberId,
    runId,
    setRunStatus,
    drawPlannedPins,
    drawTrack,
    fetchCheckpoints,
  ]);

  const fitToBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    const group = L.featureGroup([]);

    [latestMarkerRef, fromMarkerRef, toMarkerRef, stopMarkerRef].forEach((ref) => {
      if (ref.current) group.addLayer(ref.current);
    });

    if (trackLineRef.current) group.addLayer(trackLineRef.current);

    segmentLayersRef.current.forEach((layer) => group.addLayer(layer));
    checkpointMarkersRef.current.forEach((m) => group.addLayer(m));

    const bounds = group.getBounds();

    if (bounds.isValid()) map.fitBounds(bounds.pad(0.15));
    else toast("No markers to fit", { icon: "📍" });
  }, []);

  const recenter = useCallback(() => {
    const map = mapRef.current;
    if (map && latestLatLngRef.current) {
      map.panTo(latestLatLngRef.current, {
        animate: true,
        duration: 0.8,
      });
    } else {
      toast("No live position available", { icon: "📍" });
    }
  }, []);

  useEffect(() => {
    followLiveRef.current = followLive;
  }, [followLive]);

  useEffect(() => {
    if (loading) return;
    if (!mapBoxRef.current || mapRef.current) return;

    const map = L.map(mapBoxRef.current, {
      zoomControl: true,
    }).setView(DEFAULT_CENTER, 5);

    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 20,
      attribution: "&copy; OpenStreetMap",
    }).addTo(map);

    L.control.scale({ imperial: false }).addTo(map);

    const legend = L.control({ position: "bottomright" });

    legend.onAdd = () => {
      const div = L.DomUtil.create("div", "escort-legend");

      div.innerHTML = `
        <div><span style="background:#0ea5e9"></span> Live Track</div>
        <div><span style="background:#16a34a"></span> From Fixed</div>
        <div><span style="background:#dc2626"></span> To Fixed</div>
        <div><span style="background:#f97316"></span> Checkpoint</div>
        <div><span style="background:#7c3aed"></span> Stopped Here</div>
        <div><span style="background:#111827"></span> Live Vehicle</div>
      `;

      return div;
    };

    legend.addTo(map);

    const resizeMap = () => map.invalidateSize(true);

    setTimeout(resizeMap, 100);
    setTimeout(resizeMap, 500);
    setTimeout(resizeMap, 1000);

    window.addEventListener("resize", resizeMap);

    return () => {
      window.removeEventListener("resize", resizeMap);

      if (pollRef.current) clearInterval(pollRef.current);

      map.remove();
      mapRef.current = null;
    };
  }, [loading]);

  useEffect(() => {
    if (loading) return;

    fittedOnceRef.current = false;
    stableToRef.current = null;
    stableFromRef.current = null;
    checkpointsRef.current = [];
    checkpointsFetchedRef.current = false;

    setCheckpointCount(0);

    clearLayers();
    refresh();

    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(refresh, ESCORT_POLL_MS);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [loading, memberId, runId, clearLayers, refresh]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading EBD Patrol live tracking...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="escort"
      activeMenu="escort"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
        }}
      />

      <style>{`
        .escort-user-icon,
        .escort-pin-icon,
        .escort-checkpoint-icon {
          transform: translate(-50%, -50%);
        }

        .escort-legend {
          background: #ffffff;
          padding: 10px 12px;
          border-radius: 10px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.12);
          font: 12px/1.25 system-ui, Segoe UI, Roboto, Arial, sans-serif;
        }

        .escort-legend div {
          margin: 6px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .escort-legend span {
          display: inline-block;
          width: 14px;
          height: 14px;
          border-radius: 3px;
        }

        .leaflet-container {
          width: 100% !important;
          height: 100% !important;
        }

        /* Dark theme overrides */
        .escort-legend {
          background: var(--card-bg) !important;
          color: var(--text-primary) !important;
          border: 1px solid var(--card-border) !important;
        }
      `}</style>

      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.3)] overflow-hidden">
        <div className="flex items-center gap-3 border-b border-[var(--divider)] bg-[var(--card-bg)] px-4 py-3 flex-wrap">
          <div className="flex items-center gap-2 mr-1">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="26" height="26">
              <path
                d="M32 4L8 14v18c0 14 10.5 26 24 30 13.5-4 24-16 24-30V14L32 4z"
                fill="#1d4ed8"
              />
            </svg>

            <span
              className="text-base font-extrabold tracking-tight text-[var(--text-primary)]"
              style={{ fontFamily: FONT_UI }}
            >
              EBD Patrol
            </span>
          </div>

          <span className="text-[var(--text-faint)] text-base select-none">|</span>

          <h3 className="m-0 text-base font-bold text-[var(--text-primary)]">
            Live Tracking
          </h3>

          <div className="text-xs text-[var(--text-muted)]">
            Member: <b className="text-[var(--text-primary)]">{memberId || "—"}</b>
          </div>

          {runId && (
            <div className="text-xs text-[var(--text-muted)]">
              Run: <b className="text-[var(--text-primary)]">#{runId}</b>
            </div>
          )}

          <button
            onClick={() => navigate("/escort_list")}
            className="ml-auto px-4 py-2 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] text-sm font-extrabold hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] transition-colors"
          >
            Escort List
          </button>

          <div className={`rounded-full px-3 py-1 text-xs font-extrabold ${status.className}`}>
            {status.label}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 border-b border-[var(--divider)] bg-[var(--surface)] px-4 py-2">
          <Info label="Title" value={meta.title} />
          <Info label="From" value={meta.from} />
          <Info label="To" value={meta.to} />
          <Info label="Vehicle" value={meta.vehicle} />
          <Info label="Last update" value={meta.lastTs} mono />

          {checkpointCount > 0 && (
            <Info label="Checkpoints" value={String(checkpointCount)} />
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-[var(--divider)] bg-[var(--card-bg)] px-4 py-2">
          <button
            onClick={() => setFollowLive((v) => !v)}
            className="px-4 py-2 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--accent)] text-sm font-extrabold hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] transition-colors"
            style={{ 
              background: followLive ? skin.gradient : "#2563eb",
              boxShadow: followLive ? "0 4px 18px var(--accent-glow)" : "none"
            }}
          >
            {followLive ? "Follow Live" : "Paused"}
          </button>

          <button
            onClick={fitToBounds}
            className="px-4 py-2 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--accent)] text-sm font-extrabold hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] transition-colors"
          >
            Fit to Track
          </button>

          <button
            onClick={recenter}
            className="px-4 py-2 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--accent)] text-sm font-extrabold hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)] transition-colors"
          >
            Recenter
          </button>

          <div className="flex-1" />

          <Info label="Distance" value={`${meta.distanceKm} km`} />
          <Info label="Speed" value={`${meta.speedKph} km/h`} />
          <Info label="Duration" value={meta.duration} />
          <Info label="Points" value={meta.pointsCount} />
        </div>

        <div
          ref={mapBoxRef}
          className="h-[calc(100vh-235px)] min-h-[520px] w-full overflow-hidden bg-[var(--surface)]"
        />
      </div>
    </Layout>
  );
}

function Info({ label, value, mono = false }) {
  return (
    <div className="text-xs text-[var(--text-muted)]">
      <span className="mr-1">{label}:</span>
      <b className={mono ? "font-mono text-[var(--text-primary)]" : "text-[var(--text-primary)]"}>
        {value || "—"}
      </b>
    </div>
  );
}